import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import * as ReactRouter from 'react-router-dom';
import styled from 'styled-components';

import { fetchOpenKybProcesses } from 'apis/legitimizer/kyb/api';
import { getLatestKycProcess } from 'apis/legitimizer/kyc/api';
import { isKYCProcessPending } from 'apis/legitimizer/kyc/utils';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Body3, Heading5 } from 'common/typography/Typography';
import PageLoadError from 'core/PageLoadError';

import OnboardingPageHeader from './components/OnboardingPageHeader';
import { getOnboardingStatus } from '../../apis/merchant-onboarding-service/api';

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  background-color: ${cssThemedValue({ dark: cbSemanticCssVars.surfaceMain, light: cbSemanticCssVars.surfaceMain })};
`;

/* const MerchantInfoHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  font-size: 18px;
`; */

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 480px;
  margin-block-start: 180px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-inline: 16px;
    margin-block-start: 80px;
    width: 100%;
  }
`;

const SuccessImage = styled.img`
  height: 200px;
  width: 200px;
  margin: 0 auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-block-start: 24px;
  }
`;

const OnboardingPage = () => {
  const { data: user } = useCurrentUser();

  const { data: onboardingStatusData, isLoading: isFetchingOnboardingStatus } = useQuery(
    ['merchant-admin/me/onboarding-status'],
    () => getOnboardingStatus(),
  );

  const merchantId = onboardingStatusData?.merchantId;

  const { data: openKybProcesses, error: kybProcessError } = useQuery(
    ['/v1/kyb/merchants/processes/latest', merchantId],
    () => {
      if (!merchantId) {
        // Should not end up here, because the query is disabled until merchants have been fetched.
        throw new Error('Merchant ID not fetched');
      }
      return fetchOpenKybProcesses([merchantId]);
    },
    {
      enabled: !!merchantId,
      // Set cacheTime to zero to ensure that the user does not get redirected again to KYB after completing it.
      cacheTime: 0,
      // The permissions required to call this endpoint take some time to be granted to the merchant during the SSMO flow
      // Retrying is necessary for a user experience without visual errors
      retry: 20,
      retryDelay: 5000,
    },
  );

  const {
    data: kycProcess = null,
    error: kycProcessError,
    isLoading: isLoadingKycProcess,
  } = useQuery(['kyc/processes/latest'], () => getLatestKycProcess());

  const forceIDVerification = isKYCProcessPending(kycProcess);

  if (isFetchingOnboardingStatus || !openKybProcesses || isLoadingKycProcess) {
    return (
      <Root>
        <Spinner size="large" />
      </Root>
    );
  }

  if (forceIDVerification) {
    return <ReactRouter.Redirect to="/identity-verification" />;
  }

  if (openKybProcesses.length === 1) {
    return <ReactRouter.Redirect to={`/merchant/${openKybProcesses[0].merchantId}/business-verification/intro`} />;
  }

  const error = kybProcessError || kycProcessError || !merchantId || !user;

  if (error) {
    return <PageLoadError />;
  }

  return (
    <Root>
      <OnboardingPageHeader />

      <ContentWrapper>
        <SuccessImage src={`${process.env.CDN_URL}/onboarding/ssmo/success-illustration.png`} />
        <Heading5>Thanks{user.name?.first_name ? `, ${user.name.first_name}!` : '!'} We’ll take it from here.</Heading5>
        <Body3>
          We’re already at work to get your venue live. We’ll be in touch to complete the remaining steps and, once your
          business is ready, this space will become your administration platform.
        </Body3>
      </ContentWrapper>
    </Root>
  );
};

export default OnboardingPage;
